import React, { useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import { ConfirmModal } from '~/components/ConfirmModal';
import EmptyState from '~/components/core/EmptyState';
import StatusChipCore from '~/components/core/StatusChipCore';
import SortableTable from '~/components/core/Tables/SortableTable';
import { useCms } from '~/components/hooks/useCms';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';
import { isClaimWriteDisabled } from '~/Utils/ClaimUtils';

import type { DocumentRequest } from '../documentRequestDisplay';
import { canCopyLink, getLinkStatusChip, isCancelOnlyLink } from '../documentRequestDisplay';
import { TEXTS } from '../texts';
import { documentRequestCancelUrl } from '../useDocumentRequests';

import DocumentRequestLinkActions from './DocumentRequestLinkActions';

interface DocumentRequestLinksTableProps {
  requests: DocumentRequest[];
  onReload: () => Promise<unknown>;
}

const DocumentRequestLinksTable: React.FC<DocumentRequestLinksTableProps> = ({ requests, onReload }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { user } = useCms();
  // The same write gate as attach and rescan.
  const isWriteDisabled = isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true });
  const [cancelTarget, setCancelTarget] = useState<DocumentRequest | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancel = async () => {
    if (isNil(cancelTarget)) return;
    setIsCancelling(true);
    try {
      await axios.post(documentRequestCancelUrl(claim.organization_id, claim.id, cancelTarget.id));
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsCancelling(false);
      setCancelTarget(null);
      // Success shows the link as cancelled; a refusal shows whatever state the server now holds.
      await onReload();
    }
  };

  const columns = [
    {
      id: 'id',
      label: TEXTS.COLUMN_LINK_ID,
      disableSort: true,
      specialCell: (row: DocumentRequest) => TEXTS.linkIdDisplay(row.id),
    },
    {
      id: 'status',
      label: TEXTS.COLUMN_STATUS,
      disableSort: true,
      specialCell: (row: DocumentRequest) => {
        const chip = getLinkStatusChip(row);
        return <StatusChipCore status={chip.label} color={chip.color} />;
      },
    },
    {
      id: 'expires_at',
      label: TEXTS.COLUMN_EXPIRES,
      specialCell: (row: DocumentRequest) => serverDateTimeToLocal(row.expires_at),
    },
    {
      id: 'submitted_at',
      label: TEXTS.COLUMN_SUBMITTED,
      specialCell: (row: DocumentRequest) => (isNil(row.submitted_at) ? '-' : serverDateTimeToLocal(row.submitted_at)),
    },
    {
      id: 'files',
      label: TEXTS.COLUMN_FILES,
      disableSort: true,
      specialCell: (row: DocumentRequest) => row.files?.length ?? 0,
    },
    {
      id: 'actions',
      label: TEXTS.COLUMN_ACTIONS,
      disableSort: true,
      specialCell: (row: DocumentRequest) =>
        canCopyLink(row) || isCancelOnlyLink(row) ? (
          <DocumentRequestLinkActions
            organizationId={claim.organization_id}
            claimId={claim.id}
            request={row}
            isWriteDisabled={isWriteDisabled}
            isCancelDisabled={isWriteDisabled || isCancelling}
            onCancel={() => setCancelTarget(row)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <SortableTable
        rows={requests}
        // The Documents table's pagination: the same SortableTable auto-pagination, 25 a page.
        autoPaginateRowsPerPage={25}
        columns={columns}
        keepRowsOrder
        disableSortByUser
        emptyStateComponent={<EmptyState subtitle={TEXTS.LINKS_EMPTY} illustration={<></>} />}
      />
      <ConfirmModal
        isOpen={!isNil(cancelTarget)}
        isSubmitting={isCancelling}
        title={TEXTS.CANCEL_CONFIRM_TITLE}
        contentText={TEXTS.CANCEL_CONFIRM_TEXT}
        primaryButtonName={TEXTS.CANCEL_CONFIRM_BUTTON}
        secondaryButtonName={TEXTS.CANCEL_KEEP_BUTTON}
        onClose={() => setCancelTarget(null)}
        onPrimaryBtnClick={handleCancel}
      />
    </>
  );
};

export default DocumentRequestLinksTable;
